import { useState } from "react";
import Modal from "./Modal";
import Table from "./Table";
import { useDeployments } from "../../services/useDeployments";
import SearchBox from "./SearchBox";
import FilterButtons from "./FilterButtons";
import useDataRefresh from "../../services/useDataRefresh";
import { env } from "../../config";

export interface DeploymentInfo {
  branch: string;
  timestamp: string;
  deployed_by: string;
  db_triggered: boolean;
  env?: string;
  repo?: string;
  inProgress: boolean; // Added inProgress flag
  status: string; // Added status flag
}

interface FlattenedRepo {
  repoName: string;
  groupName: string;
}

interface MainPageProps {}

export interface logData {
  repoName: string;
  envName: string;
}

const Index = (props: MainPageProps) => {
  const { deploymentTable, envs, repos, isLoading, fetchData } =
    useDeployments();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [logData, setLogData] = useState<logData | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedFilter, setSelectedFilter] = useState<string>("All");

  const handleClick = (
    repoName: string,
    envName: string,
    deploymentInfo: DeploymentInfo | null
  ) => {
    if (deploymentInfo) {
      setLogData({ repoName, envName });
      setShowModal(true);
    } else {
      setLogData(null);
      setShowModal(true);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setLogData(null);
  };

  const groupedRepos = repos.reduce<Record<string, FlattenedRepo[]>>(
    (acc, repo) => {
      if (!acc[repo.groupName]) {
        acc[repo.groupName] = [];
      }
      acc[repo.groupName].push(repo);
      return acc;
    },
    {}
  );

  const groupedReposArray = Object.entries(groupedRepos);

  const filteredReposArray: [string, FlattenedRepo[]][] = groupedReposArray
    .map(([groupName, groupRepos]): [string, FlattenedRepo[]] => [
      groupName,
      groupRepos.filter((repo) => {
        const repoParts = repo.repoName.toLowerCase().split("-");

        const matchesSearch = repoParts.some((part) =>
          part.includes(searchQuery.toLowerCase())
        );
        const matchesFilter =
          selectedFilter === "All" ||
          (selectedFilter.toLowerCase() === "ui" && repoParts.includes("ui")) ||
          (selectedFilter.toLowerCase() === "api" &&
            repoParts.includes("api")) ||
          (selectedFilter.toLowerCase() === "mob" && repoParts.includes("mob"));

        return matchesSearch && matchesFilter;
      }),
    ])
    .filter(([, groupRepos]) => groupRepos.length > 0);

  // Use custom refresh hook
  useDataRefresh(fetchData, Number(env.REFRESH_TIME));

  return (
    <>
      <div className="flex gap-2 justify-between items-center mb-2 px-4">
        <h3 className="font-semibold">Deployment Grid</h3>
        <div className="flex gap-2">
          <SearchBox
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
          />
          <FilterButtons
            selectedFilter={selectedFilter}
            setSelectedFilter={setSelectedFilter}
          />
        </div>
      </div>

      {/* Table */}
      <Table
        data={deploymentTable}
        envs={envs}
        repos={filteredReposArray}
        Loading={isLoading}
        handleClick={handleClick}
        searchQuery={searchQuery}
        selectedFilter={selectedFilter}
      />

      {/* Modal */}
      {logData && (
        <Modal
          show={showModal}
          repo={logData.repoName}
          env={logData.envName}
          onClose={closeModal}
        />
      )}
    </>
  );
};

export default Index;
